/* eslint-disable @typescript-eslint/no-magic-numbers */
import * as react from 'react';
import * as reactDom from 'react-dom';
import * as reactDomServer from 'react-dom/server';

import { Bean, MergeAll } from '~libs.typescript';

const {
   Children,
   cloneElement,
   Component,
   createContext,
   createElement,
   forwardRef,
   Fragment,
   isValidElement,
   lazy,
   memo,
   StrictMode,
   useCallback,
   Suspense,
   useContext,
   useEffect,
   useImperativeHandle,
   useLayoutEffect,
   useMemo,
   useReducer,
   useRef,
   useState,
} = react;

const { createPortal } = reactDom;

const { renderToString } = reactDomServer;

export {
   Children,
   cloneElement,
   Component,
   createContext,
   createElement,
   createPortal,
   forwardRef,
   Fragment,
   isValidElement,
   lazy,
   memo,
   StrictMode,
   Suspense,
   useCallback,
   useContext,
   useEffect,
   useImperativeHandle,
   useLayoutEffect,
   useMemo,
   useReducer,
   useRef,
   useState,
   renderToString,
};

export type {
   ChangeEvent,
   ChangeEventHandler,
   ComponentType,
   DependencyList,
   Dispatch,
   EffectCallback,
   ElementType,
   TextareaHTMLAttributes,
   FocusEventHandler,
   FocusEvent,
   FormEventHandler,
   InputHTMLAttributes,
   FormHTMLAttributes,
   KeyboardEventHandler,
   MouseEventHandler,
   MutableRefObject,
   PointerEvent,
   SyntheticEvent,
   PointerEventHandler,
   ReactElement,
   ReactNode,
   ReactNodeArray,
   ReactPortal,
   RefObject,
   EventHandler,
   SelectHTMLAttributes,
   SetStateAction,
   TransitionEventHandler,
   ImgHTMLAttributes,
} from 'react';

export type Props<PROPS extends Bean> = Readonly<PROPS>;
export type MergeProps<PROPS extends Bean[]> = Props<MergeAll<PROPS>>;
export type Prop<NAME extends string, VALUE> = { [K in NAME]?: VALUE };
export type ChildrenNode = react.ReactNode | react.ReactNodeArray | JSX.Element;
export type ChildrenProp = Prop<'children', ChildrenNode>;
export type JsxChildrenProp = Prop<'children', JSX.Element>;

export type ComponentName = string;
export type FunctionalComponent<PROPS extends Bean | unknown = unknown> = (props: PROPS) => JSX.Element | null;
export type HTMLElements = Omit<HTMLElementTagNameMap, 'search'>;
export type HTMLElementTag = keyof HTMLElements;
export type HTMLElementId = string;
export type HTMLElementProps<TAG extends HTMLElementTag> = HTMLElements[TAG];
export type HTMLElementRef<TAG extends HTMLElementTag = HTMLElementTag> = react.MutableRefObject<
   HTMLElements[TAG] | null
>;
export type ChildrenRenderer<PROPS extends Bean> = (props: PROPS) => JSX.Element;
export type ChildrenRendererProp<PROPS extends Bean> = Prop<'children', ChildrenRenderer<PROPS>>;
export type ElementInstance<TAG extends HTMLElementTag> = (instance: HTMLElements[TAG] | null) => void;
export type ElementRef<TAG extends HTMLElementTag> =
   | react.MutableRefObject<HTMLElements[TAG] | null>
   | ElementInstance<TAG>;
export type ElementRefProp<TAG extends HTMLElementTag> = Prop<'elementRef', ElementRef<TAG>>;
export type ElementTagProp<TAG extends HTMLElementTag = HTMLElementTag> = Prop<'elementTag', TAG>;
export const EmptyComponent = (): null => null;
